import {
	Check,
	Delete,
	DriveFileRenameOutline,
	FolderOpen,
} from "@mui/icons-material";
import {
	Box,
	Checkbox,
	IconButton,
	MenuItem,
	Stack,
	Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { FC, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { ESRThunkDispatch, renameFolder } from "../../../actions/form";
import { ESRState } from "../../../types/data";
import { Layout } from "./Layout";
import { ClowderFolder } from "./types";

type Props = {
	disabled?: boolean;
	folder: ClowderFolder;
	onClick?: () => void;
	onDelete: () => void;
};

export const FolderItem: FC<Props> = ({
	disabled = false,
	folder,
	onClick,
	onDelete,
}) => {
	const { esrId = "" } = useParams();
	const dispatch = useDispatch<ESRThunkDispatch>();
	const [editing, setEditing] = useState(false);
	const [newName, setNewName] = useState(folder.name);
	const path = useSelector((state: ESRState) => state.current_folder.path);
	const clipboard = useSelector((state: ESRState) => state.clipboard);
	const currentFolder = useSelector((state: ESRState) => state.current_folder);

	const updateFolderName = useCallback(() => {
		setEditing(false);
		dispatch(renameFolder(esrId, folder.id, newName));
	}, [esrId, folder.id, newName]);

	return (
		<MenuItem disabled={disabled} onClick={onClick}>
			<Layout
				leftActions={
					<Checkbox
						size="small"
						sx={{
							visibility:
								disabled || currentFolder.read_only ? "hidden" : "visible",
						}}
						checked={!!clipboard.find((item) => item.id === folder.id)}
						onClick={(e) => e.stopPropagation()}
						onChange={(e) => {
							dispatch({
								type: "UPDATE_CLIPBOARD",
								update: e.target.checked
									? [
											...clipboard,
											{
												type: "folder",
												name: folder.name,
												id: folder.id,
											},
										]
									: clipboard.filter((item) => item.id !== folder.id),
							});
						}}
					/>
				}
				icon={<FolderOpen />}
				content={
					editing ? (
						<TextField
							variant="standard"
							value={newName}
							onClick={(e) => {
								e.stopPropagation();
							}}
							onChange={(e) => {
								setNewName(e.target.value);
							}}
							onKeyUp={(e) => {
								if (e.key === "Enter") {
									updateFolderName();
								}
							}}
						/>
					) : (
						<Box>
							<Typography fontSize={14} fontWeight={500}>
								{folder.name}
							</Typography>
							<Typography fontSize={12} color="gray" fontWeight={500}>
								Folder
							</Typography>
						</Box>
					)
				}
				rightActions={
					!currentFolder.read_only && (
						<Stack direction="row">
							{editing ? (
								<IconButton
									disabled={disabled}
									onClick={(e) => {
										e.stopPropagation();
										updateFolderName();
									}}
								>
									<Check />
								</IconButton>
							) : (
								<IconButton
									disabled={disabled}
									onClick={(e) => {
										e.stopPropagation();
										setNewName(folder.name);
										setEditing(true);
									}}
								>
									<DriveFileRenameOutline />
								</IconButton>
							)}
							<IconButton
								aria-label="delete folder"
								color="error"
								disabled={disabled}
								onClick={(e) => {
									e.stopPropagation();
									if (confirm("Are you sure you want to delete?")) {
										onDelete();
									}
								}}
							>
								<Delete />
							</IconButton>
						</Stack>
					)
				}
				created={folder.created}
			/>
		</MenuItem>
	);
};
